import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Loader2, ArrowLeft, Mail, MessageSquare, MailCheck } from 'lucide-react';
import { authApi } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const searchParams = new URLSearchParams(location.search);
  const from = location.state?.from?.pathname || searchParams.get('redirect') || '/';

  // Steps:
  //  'choose'    — passwordless: enter email, pick link or code (default)
  //  'link-sent' — confirmation that the email link was sent
  //  'code'      — enter the SMS one-time login code
  //  'password'  — password fallback (email + password)
  //  '2fa'       — password fallback's SMS 2FA step
  //  'forgot'    — password reset
  const [step, setStep] = useState('choose');
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  // Arriving from an activation link for an ALREADY-set-up account: the
  // activate page redirects here (?info=already_activated) — explain the
  // jump so it doesn't read as the link being broken.
  const [successMessage, setSuccessMessage] = useState(
    searchParams.get('info') === 'already_activated'
      ? 'Your account is already set up — just sign in below with your email address.'
      : ''
  );
  const [isLoading, setIsLoading] = useState(false);

  const nextParam = from && from !== '/' ? from : null;

  const finishLogin = (result) => {
    login(result.access_token, result.user);
    if (result.user?.must_change_password) navigate('/change-password', { replace: true });
    else navigate(from, { replace: true });
  };

  // Validate the identifier BEFORE any request goes out. A client typed
  // her phone number here, got the (deliberately) generic "if that email
  // is on file..." response, and never got in — the account-privacy
  // message is correct, so the only place to catch the mistake is the
  // input itself. Phone-shaped input gets a specific explanation; other
  // non-email input gets a plain ask. The inputs are type="text" (not
  // type="email") on purpose — some buttons here aren't form submits, so
  // native validation never covered every path anyway.
  const identifierProblem = (value) => {
    const v = (value || '').trim();
    const digits = v.replace(/[^\d]/g, '');
    if (digits.length >= 7 && /^[\d\s().+-]+$/.test(v)) {
      return "That looks like a phone number. Enter your EMAIL address instead — if you choose \"Text me a code\", the code goes to the phone number on your account.";
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v)) {
      return 'Please enter a valid email address (like you@example.com).';
    }
    return null;
  };

  // ---- Passwordless: request a magic link by email -------------------------
  const handleRequestEmailLink = async (e) => {
    e.preventDefault();
    if (!email) return;
    const problem = identifierProblem(email);
    if (problem) { setError(problem); return; }
    setError(''); setSuccessMessage(''); setIsLoading(true);
    try {
      const resp = await authApi.requestLoginLink(email, 'email', nextParam);
      // A 400 (input-shape rejection) comes back as {detail} — surface it
      // instead of pretending a link went out.
      if (resp?.detail) { setError(resp.detail); return; }
      setStep('link-sent');
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ---- Passwordless: request a one-time code by SMS ------------------------
  const handleRequestSmsCode = async () => {
    if (!email) { setError('Enter your email first.'); return; }
    const problem = identifierProblem(email);
    if (problem) { setError(problem); return; }
    setError(''); setSuccessMessage(''); setIsLoading(true);
    try {
      const resp = await authApi.requestLoginLink(email, 'sms');
      if (resp?.detail) { setError(resp.detail); return; }
      setStep('code');
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ---- Passwordless: verify the SMS code -----------------------------------
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError(''); setIsLoading(true);
    try {
      const result = await authApi.verifyLoginCode(email, code);
      if (result.access_token) finishLogin(result);
      else setError(result.detail || 'Invalid or expired code.');
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ---- Password fallback ---------------------------------------------------
  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    const problem = identifierProblem(email);
    if (problem) { setError(problem); return; }
    setError(''); setIsLoading(true);
    try {
      const result = await authApi.login(email, password);
      if (result.detail) setError(result.detail);
      else if (result.requires_2fa) setStep('2fa');
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setError(''); setIsLoading(true);
    try {
      const result = await authApi.verify2FA(email, code);
      if (result.detail) setError(result.detail);
      else if (result.access_token) finishLogin(result);
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setError(''); setIsLoading(true);
    try {
      await authApi.resend2FA(email);
    } catch (err) {
      setError('Failed to resend code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError(''); setSuccessMessage(''); setIsLoading(true);
    try {
      const result = await authApi.forgotPassword(email);
      if (result.detail) setError(result.detail);
      else setSuccessMessage('A temporary password has been sent to your phone. Use it to sign in, then set a new password.');
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const headerSubtitle = {
    choose: 'Sign in to your account',
    'link-sent': 'Check your email',
    code: 'Enter your login code',
    password: 'Sign in with your password',
    '2fa': 'Enter verification code',
    forgot: 'Reset your password',
  }[step];

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img src="/logo.svg" alt="Macula Networks" className="portal-logo" />
          <h1>Client Portal</h1>
          <p>{headerSubtitle}</p>
        </div>

        {error && <div className="error-message">{error}</div>}
        {successMessage && <div className="success-message">{successMessage}</div>}

        {/* ---- Passwordless chooser (default) ---- */}
        {step === 'choose' && (
          <form onSubmit={handleRequestEmailLink} className="login-form">
            <div className="form-group">
              <label htmlFor="portal-email">Email</label>
              <input
                type="text"
                inputMode="email"
                id="portal-email"
                name="portal-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
              />
            </div>

            <button type="submit" className="submit-button" disabled={isLoading || !email}>
              {isLoading ? <Loader2 className="spinner" size={20} /> : <><Mail size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />Email me a login link</>}
            </button>

            <button
              type="button"
              className="resend-button"
              onClick={handleRequestSmsCode}
              disabled={isLoading}
              style={{ marginTop: 10 }}
            >
              <MessageSquare size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Text me a code instead
            </button>

            <p style={{ fontSize: 13, color: '#94a3b8', marginTop: 14, textAlign: 'center' }}>
              No password needed — we’ll send you a one-time link or code.
            </p>

            <button
              type="button"
              className="forgot-password-link"
              onClick={() => { setStep('password'); setError(''); setSuccessMessage(''); }}
            >
              Use a password instead
            </button>
          </form>
        )}

        {/* ---- Email link sent confirmation ---- */}
        {step === 'link-sent' && (
          <div className="login-form" style={{ textAlign: 'center' }}>
            <MailCheck size={48} style={{ color: '#0ea5e9', margin: '8px auto 16px' }} />
            <p className="code-info">
              If <strong>{email}</strong> is on file, we’ve sent a login link.
              Open it on this device to sign in. The link expires in 36 hours.
            </p>
            <button type="button" className="resend-button" onClick={handleRequestEmailLink} disabled={isLoading}>
              Resend link
            </button>
            <button
              type="button"
              className="back-button"
              onClick={() => { setStep('choose'); setError(''); }}
              style={{ marginTop: 12 }}
            >
              <ArrowLeft size={16} /> Back
            </button>
          </div>
        )}

        {/* ---- SMS login code entry ---- */}
        {step === 'code' && (
          <form onSubmit={handleVerifyCode} className="login-form">
            <button
              type="button"
              className="back-button"
              onClick={() => { setStep('choose'); setCode(''); setError(''); }}
            >
              <ArrowLeft size={16} /> Back
            </button>
            <p className="code-info">
              If a mobile number is on file for <strong>{email}</strong>, we’ve texted a 6-digit code. Enter it below.
            </p>
            <div className="form-group">
              <label htmlFor="code">Login Code</label>
              <input
                type="text"
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                required
                autoComplete="one-time-code"
                maxLength={6}
                className="code-input"
              />
            </div>
            <button type="submit" className="submit-button" disabled={isLoading}>
              {isLoading ? <Loader2 className="spinner" size={20} /> : 'Sign In'}
            </button>
            <button type="button" className="resend-button" onClick={handleRequestSmsCode} disabled={isLoading}>
              Resend Code
            </button>
          </form>
        )}

        {/* ---- Password fallback: credentials ---- */}
        {step === 'password' && (
          <form onSubmit={handleCredentialsSubmit} className="login-form">
            <button
              type="button"
              className="back-button"
              onClick={() => { setStep('choose'); setError(''); }}
            >
              <ArrowLeft size={16} /> Back
            </button>
            <div className="form-group">
              <label htmlFor="portal-email-pw">Email</label>
              <input
                type="text"
                inputMode="email"
                id="portal-email-pw"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="password-input">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <button type="submit" className="submit-button" disabled={isLoading}>
              {isLoading ? <Loader2 className="spinner" size={20} /> : 'Sign In'}
            </button>
            <button
              type="button"
              className="forgot-password-link"
              onClick={() => { setStep('forgot'); setError(''); setSuccessMessage(''); }}
            >
              Forgot Password?
            </button>
          </form>
        )}

        {/* ---- Password fallback: 2FA ---- */}
        {step === '2fa' && (
          <form onSubmit={handle2FASubmit} className="login-form">
            <button
              type="button"
              className="back-button"
              onClick={() => { setStep('password'); setCode(''); setError(''); }}
            >
              <ArrowLeft size={16} /> Back
            </button>
            <p className="code-info">
              We've sent a verification code to your phone. Enter it below to complete sign in.
            </p>
            <div className="form-group">
              <label htmlFor="code-2fa">Verification Code</label>
              <input
                type="text"
                id="code-2fa"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                required
                autoComplete="one-time-code"
                maxLength={6}
                className="code-input"
              />
            </div>
            <button type="submit" className="submit-button" disabled={isLoading}>
              {isLoading ? <Loader2 className="spinner" size={20} /> : 'Verify'}
            </button>
            <button type="button" className="resend-button" onClick={handleResend} disabled={isLoading}>
              Resend Code
            </button>
          </form>
        )}

        {/* ---- Forgot password ---- */}
        {step === 'forgot' && (
          <form onSubmit={handleForgotPassword} className="login-form">
            <button
              type="button"
              className="back-button"
              onClick={() => { setStep('password'); setError(''); setSuccessMessage(''); }}
            >
              <ArrowLeft size={16} /> Back to Sign In
            </button>
            <p className="code-info">
              Enter your email address. If your account exists, a temporary password will be sent to your phone via SMS.
            </p>
            <div className="form-group">
              <label htmlFor="forgot-email">Email</label>
              <input
                type="text"
                inputMode="email"
                id="forgot-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
              />
            </div>
            <button type="submit" className="submit-button" disabled={isLoading}>
              {isLoading ? <Loader2 className="spinner" size={20} /> : 'Send Temporary Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Login;
